import { useState } from 'react';
import {
  Card,
  Typography,
  Box,
  Button,
  Chip,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Stack,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  Alert,
  AlertTitle,
  Badge,
  Tooltip,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  IoEllipsisVertical,
  IoAdd,
  IoSend,
  IoHammer,
  IoCheckmark,
  IoClose,
  IoRefresh,
  IoTime,
  IoWarning,
  IoAlertCircle,
  IoFilter,
  IoDownload,
  IoEye,
  IoCalendar,
  IoPersonCircle,
} from 'react-icons/io5';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow, format } from 'date-fns';

import { transactionService } from 'src/services/transaction.service';
import { ToastNotifications } from 'src/components';
import { Tables } from 'src/types/db.types';

interface TeacherTransactionManagerProps {
  teacherId: string;
  classIds?: string[];
}

type TransactionWithStudent = Tables<'transactions'> & {
  transaction_type: Tables<'transaction_types'>;
  student: {
    id: string;
    user: {
      firstname: string;
      lastname: string;
    };
  };
};

type TabValue = 'all' | 'pending' | 'overdue' | 'completed';
type StatusFilter = 'all' | 'PENDING_STUDENT_ACTION' | 'DEFERRED' | 'APPROVED' | 'CANCELLED';

interface TabPanelProps {
  children?: React.ReactNode;
  index: TabValue;
  value: TabValue;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`teacher-transaction-tabpanel-${index}`}
      aria-labelledby={`teacher-transaction-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function TeacherTransactionManager({ teacherId, classIds }: TeacherTransactionManagerProps) {
  const [activeTab, setActiveTab] = useState<TabValue>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<HTMLElement | null>(null);
  
  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  
  // Form states
  const [overrideAction, setOverrideAction] = useState<'approve' | 'cancel'>('approve');
  const [overrideReason, setOverrideReason] = useState('');
  const [nudgeMessage, setNudgeMessage] = useState('');
  const [selectedTransactions, setSelectedTransactions] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  const queryClient = useQueryClient();

  // Fetch transactions based on teacher and classes
  const {
    data: transactions = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['teacher-transactions', teacherId, classIds, statusFilter],
    queryFn: async () => {
      // Mock implementation - in production this would query by teacher's students
      const mockTransactions: TransactionWithStudent[] = [
        {
          id: '1',
          amount: 25,
          transaction_status: 'PENDING_STUDENT_ACTION',
          createdat: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
          updatedat: new Date().toISOString(),
          studentid: 'student1',
          transactiontypeid: 'type1',
          late_fee_total: 0,
          deferred_count: 0,
          transaction_type: {
            id: 'type1',
            name: 'Late Assignment Fine',
            type: 'debit',
            createdat: new Date().toISOString(),
            updatedat: new Date().toISOString(),
            points: 25,
            deletedat: null,
            description: 'Penalty for late submissions',
            extendedproperties: null,
            priceType: null,
            charge_mode: 'bill',
          },
          student: {
            id: 'student1',
            user: { firstname: 'John', lastname: 'Doe' },
          },
          balanceafter: null,
          deletedat: null,
          description: 'Assignment submitted 2 days late',
          due_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Due tomorrow
          extendedproperties: null,
          original_amount: null,
          payment_method: null,
          reason: 'Late submission penalty',
          rule_applied_id: null,
          savings_allocation: null,
          status: 'Pending',
          student_action: null,
          student_action_date: null,
          teacher_override_reason: null,
        },
        {
          id: '2',
          amount: 15,
          transaction_status: 'DEFERRED',
          createdat: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days ago
          updatedat: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          studentid: 'student2',
          transactiontypeid: 'type2',
          late_fee_total: 2.5,
          deferred_count: 1,
          transaction_type: {
            id: 'type2',
            name: 'Participation Points',
            type: 'credit',
            createdat: new Date().toISOString(),
            updatedat: new Date().toISOString(),
            points: 15,
            deletedat: null,
            description: 'Class participation reward',
            extendedproperties: null,
            priceType: null,
            charge_mode: 'bill',
          },
          student: {
            id: 'student2',
            user: { firstname: 'Jane', lastname: 'Smith' },
          },
          balanceafter: null,
          deletedat: null,
          description: 'Great participation in class discussion',
          due_date: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(), // Overdue
          extendedproperties: null,
          original_amount: null,
          payment_method: null,
          reason: 'Active class participation',
          rule_applied_id: null,
          savings_allocation: null,
          status: 'Deferred',
          student_action: 'defer',
          student_action_date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          teacher_override_reason: null,
        },
      ];
      
      // Filter based on statusFilter
      if (statusFilter === 'all') return mockTransactions;
      return mockTransactions.filter(t => t.transaction_status === statusFilter);
    },
    enabled: !!teacherId,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Get filtered transactions based on active tab
  const getFilteredTransactions = () => {
    switch (activeTab) {
      case 'pending':
        return transactions.filter(t => t.transaction_status === 'PENDING_STUDENT_ACTION');
      case 'overdue':
        return transactions.filter(t => 
          t.transaction_status === 'DEFERRED' && 
          t.due_date && 
          new Date(t.due_date) < new Date()
        );
      case 'completed':
        return transactions.filter(t => 
          ['APPROVED', 'CANCELLED'].includes(t.transaction_status)
        );
      default:
        return transactions;
    }
  };

  const filteredTransactions = getFilteredTransactions();

  // Calculate summary stats
  const stats = {
    total: transactions.length,
    pending: transactions.filter(t => t.transaction_status === 'PENDING_STUDENT_ACTION').length,
    overdue: transactions.filter(t => 
      t.transaction_status === 'DEFERRED' && 
      t.due_date && 
      new Date(t.due_date) < new Date()
    ).length,
    completed: transactions.filter(t => 
      ['APPROVED', 'CANCELLED'].includes(t.transaction_status)
    ).length,
  };

  // Mutations
  const overrideTransactionMutation = useMutation({
    mutationFn: ({ transactionId, action, reason }: {
      transactionId: string;
      action: 'approve' | 'cancel';
      reason: string;
    }) => transactionService.overrideTransaction(transactionId, teacherId, action, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacher-transactions'] });
      ToastNotifications.success('Transaction override applied successfully!');
      handleCloseOverrideModal();
    },
    onError: (error: any) => {
      ToastNotifications.error(error.message || 'Failed to override transaction');
    },
  });

  const sendNudgeMutation = useMutation({
    mutationFn: ({ transactionId, message }: { transactionId: string; message: string }) => {
      // Mock implementation - would integrate with notification service
      return new Promise(resolve => setTimeout(resolve, 1000));
    },
    onSuccess: () => {
      ToastNotifications.success('Reminder sent to student!');
      handleActionMenuClose();
    },
  });

  // Event handlers
  const handleTabChange = (event: React.SyntheticEvent, newValue: TabValue) => {
    setActiveTab(newValue);
  };

  const handleActionMenuOpen = (event: React.MouseEvent<HTMLElement>, transactionId: string) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedTransactionId(transactionId);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedTransactionId(null);
  };

  const handleOverrideTransaction = (action: 'approve' | 'cancel') => {
    setOverrideAction(action);
    setIsOverrideModalOpen(true);
    handleActionMenuClose();
  };

  const handleCloseOverrideModal = () => {
    setIsOverrideModalOpen(false);
    setOverrideReason('');
    setSelectedTransactionId(null);
  };

  const handleConfirmOverride = () => {
    if (!selectedTransactionId || !overrideReason.trim()) {
      ToastNotifications.error('Please provide a reason for the override');
      return;
    }

    overrideTransactionMutation.mutate({
      transactionId: selectedTransactionId,
      action: overrideAction,
      reason: overrideReason,
    });
  };

  const handleSendNudge = () => {
    if (!selectedTransactionId) return;
    
    sendNudgeMutation.mutate({
      transactionId: selectedTransactionId,
      message: nudgeMessage || 'Please respond to your pending transaction.',
    });
  };

  const getStatusColor = (transaction: TransactionWithStudent) => {
    switch (transaction.transaction_status) {
      case 'PENDING_STUDENT_ACTION':
        return 'warning';
      case 'DEFERRED':
        return transaction.due_date && new Date(transaction.due_date) < new Date() ? 'error' : 'info';
      case 'APPROVED':
        return 'success';
      case 'CANCELLED':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (transaction: TransactionWithStudent) => {
    switch (transaction.transaction_status) {
      case 'PENDING_STUDENT_ACTION':
        return 'Awaiting Student';
      case 'DEFERRED':
        if (transaction.due_date && new Date(transaction.due_date) < new Date()) {
          return 'Overdue';
        }
        return `Due ${formatDistanceToNow(new Date(transaction.due_date!), { addSuffix: true })}`;
      case 'APPROVED':
        return 'Completed';
      case 'CANCELLED':
        return 'Cancelled';
      default:
        return transaction.transaction_status;
    }
  };

  const getUrgencyIcon = (transaction: TransactionWithStudent) => {
    if (transaction.transaction_status === 'DEFERRED' && transaction.due_date) {
      const hoursUntilDue = (new Date(transaction.due_date).getTime() - Date.now()) / (1000 * 60 * 60);
      if (hoursUntilDue < 0) return <IoWarning color="#f44336" />;
      if (hoursUntilDue < 24) return <IoTime color="#ff9800" />;
    }
    return null;
  };

  if (error) {
    return (
      <Alert severity="error">
        <AlertTitle>Error Loading Transactions</AlertTitle>
        Unable to load transaction data. Please try refreshing.
      </Alert>
    );
  }

  return (
    <Card sx={{ borderRadius: 3 }}>
      <Box p={3}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight="bold">
            Transaction Management
          </Typography>
          <Box display="flex" gap={1}>
            <Tooltip title="Toggle filters">
              <IconButton onClick={() => setShowFilters(!showFilters)}>
                <IoFilter />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton onClick={() => refetch()}>
                <IoRefresh />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<IoDownload />}
              size="small"
            >
              Export
            </Button>
            <Button
              variant="contained"
              startIcon={<IoAdd />}
              onClick={() => setIsCreateModalOpen(true)}
            >
              New Transaction
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        {showFilters && (
          <Card sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" gutterBottom>
              Filters
            </Typography>
            <Box display="flex" gap={2} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                  label="Status"
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="PENDING_STUDENT_ACTION">Pending</MenuItem>
                  <MenuItem value="DEFERRED">Deferred</MenuItem>
                  <MenuItem value="APPROVED">Approved</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                </Select>
              </FormControl>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={selectedTransactions.length > 0}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        setSelectedTransactions([]);
                      }
                    }}
                  />
                }
                label="Bulk Actions"
              />
              
              {selectedTransactions.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setIsBulkModalOpen(true)}
                >
                  Actions ({selectedTransactions.length})
                </Button>
              )}
            </Box>
          </Card>
        )}

        {/* Summary Stats */}
        <Box display="flex" gap={2} mb={3}>
          <Card sx={{ p: 2, flex: 1, bgcolor: 'primary.50' }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Badge badgeContent={stats.total} color="primary">
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <IoPersonCircle />
                </Avatar>
              </Badge>
              <Box>
                <Typography variant="h6">{stats.total}</Typography>
                <Typography variant="caption">Total</Typography>
              </Box>
            </Box>
          </Card>

          <Card sx={{ p: 2, flex: 1, bgcolor: 'warning.50' }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Badge badgeContent={stats.pending} color="warning">
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <IoTime />
                </Avatar>
              </Badge>
              <Box>
                <Typography variant="h6">{stats.pending}</Typography>
                <Typography variant="caption">Pending</Typography>
              </Box>
            </Box>
          </Card>

          <Card sx={{ p: 2, flex: 1, bgcolor: 'error.50' }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Badge badgeContent={stats.overdue} color="error">
                <Avatar sx={{ bgcolor: 'error.main' }}>
                  <IoWarning />
                </Avatar>
              </Badge>
              <Box>
                <Typography variant="h6">{stats.overdue}</Typography>
                <Typography variant="caption">Overdue</Typography>
              </Box>
            </Box>
          </Card>

          <Card sx={{ p: 2, flex: 1, bgcolor: 'success.50' }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: 'success.main' }}>
                <IoCheckmark />
              </Avatar>
              <Box>
                <Typography variant="h6">{stats.completed}</Typography>
                <Typography variant="caption">Completed</Typography>
              </Box>
            </Box>
          </Card>
        </Box>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label={`All (${stats.total})`} value="all" />
          <Tab label={`Pending (${stats.pending})`} value="pending" />
          <Tab label={`Overdue (${stats.overdue})`} value="overdue" />
          <Tab label={`Completed (${stats.completed})`} value="completed" />
        </Tabs>

        {/* Transaction Table */}
        <TabPanel value={activeTab} index={activeTab}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              Loading transactions...
            </Box>
          ) : filteredTransactions.length === 0 ? (
            <Box textAlign="center" py={4}>
              <IoPersonCircle size={48} color="#757575" />
              <Typography variant="h6" mt={2}>No Transactions Found</Typography>
              <Typography variant="body2" color="text.secondary">
                No transactions match the current filter criteria.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Student</TableCell>
                    <TableCell>Transaction</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Response Time</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTransactions.map((transaction) => {
                    const responseTime = transaction.student_action_date
                      ? Math.round((new Date(transaction.student_action_date).getTime() - new Date(transaction.createdat).getTime()) / (1000 * 60 * 60))
                      : null;

                    return (
                      <TableRow key={transaction.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
                              {transaction.student.user.firstname[0]}{transaction.student.user.lastname[0]}
                            </Avatar>
                            <Typography variant="body2">
                              {transaction.student.user.firstname} {transaction.student.user.lastname}
                            </Typography>
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {transaction.transaction_type.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {transaction.description || transaction.reason}
                            </Typography>
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            color={transaction.transaction_type.type === 'debit' ? 'error.main' : 'success.main'}
                            fontWeight="medium"
                          >
                            {transaction.transaction_type.type === 'debit' ? '-' : '+'}${transaction.amount}
                            {transaction.late_fee_total > 0 && (
                              <Typography variant="caption" color="error.main" display="block">
                                +${transaction.late_fee_total} late fee
                              </Typography>
                            )}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                              label={getStatusLabel(transaction)}
                              color={getStatusColor(transaction) as any}
                              size="small"
                            />
                            {getUrgencyIcon(transaction)}
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {responseTime ? `${responseTime}h` : 'Pending'}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(transaction.createdat), 'MMM dd, yyyy')}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={(e) => handleActionMenuOpen(e, transaction.id)}
                          >
                            <IoEllipsisVertical />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
      >
        <MenuItem onClick={handleSendNudge}>
          <IoSend style={{ marginRight: 8 }} />
          Send Reminder
        </MenuItem>
        <MenuItem onClick={() => handleOverrideTransaction('approve')}>
          <IoCheckmark style={{ marginRight: 8 }} />
          Force Approve
        </MenuItem>
        <MenuItem onClick={() => handleOverrideTransaction('cancel')}>
          <IoClose style={{ marginRight: 8 }} />
          Cancel Transaction
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleActionMenuClose}>
          <IoEye style={{ marginRight: 8 }} />
          View Details
        </MenuItem>
      </Menu>

      {/* Override Modal */}
      <Dialog
        open={isOverrideModalOpen}
        onClose={handleCloseOverrideModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <Typography variant="h6">
            {overrideAction === 'approve' ? 'Force Approve' : 'Cancel'} Transaction
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Stack spacing={3}>
            <Alert severity="warning">
              <AlertTitle>Teacher Override</AlertTitle>
              This action will override the student's decision and cannot be undone. 
              Please provide a clear reason for this override.
            </Alert>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason for Override"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="Explain why you're overriding this transaction..."
            />

            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button onClick={handleCloseOverrideModal}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color={overrideAction === 'approve' ? 'success' : 'error'}
                onClick={handleConfirmOverride}
                disabled={!overrideReason.trim() || overrideTransactionMutation.isPending}
                startIcon={<IoHammer />}
              >
                {overrideAction === 'approve' ? 'Force Approve' : 'Cancel Transaction'}
              </Button>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    </Card>
  );
}